import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
};
const AuthContext = createContext(undefined);
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
export const AuthProvider = ({ children }) => {
    const [state, setState] = useState(initialState);
    const navigate = useNavigate();
    // Session validation on mount
    useEffect(() => {
        const validateSession = async () => {
            try {
                const response = await fetch('/api/auth/validate', {
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.authenticated && data.user) {
                        setState(prev => ({
                            ...prev,
                            user: data.user || null,
                            isAuthenticated: true,
                            isLoading: false
                        }));
                    }
                    else {
                        setState(prev => ({
                            ...prev,
                            user: null,
                            isAuthenticated: false,
                            isLoading: false
                        }));
                    }
                }
                else {
                    setState(prev => ({
                        ...prev,
                        user: null,
                        isAuthenticated: false,
                        isLoading: false
                    }));
                }
            }
            catch (error) {
                console.error('Session validation error:', error);
                setState(prev => ({
                    ...prev,
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    error: 'Session validation failed'
                }));
            }
        };
        validateSession();
    }, []);
    const setLoading = (loading) => {
        setState(prev => ({ ...prev, isLoading: loading, error: null }));
    };
    const setError = (error) => {
        setState(prev => ({ ...prev, error, isLoading: false }));
    };
    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: null }));
    }, []);
    const login = useCallback(async (credentials) => {
        try {
            setLoading(true);
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials),
                credentials: 'include',
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Login failed');
            }
            setState(prev => ({
                ...prev,
                user: data.user || null,
                isAuthenticated: true,
                isLoading: false,
                error: null
            }));
            navigate('/', { replace: true });
        }
        catch (error) {
            setError(error instanceof Error ? error.message : 'Login failed');
        }
    }, [navigate]);
    const register = useCallback(async (credentials) => {
        try {
            setLoading(true);
            const response = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Registration failed');
            }
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: null,
                user: null,
                isAuthenticated: false
            }));
            // Registration successful - let the UI handle the success message
            // Removed the alert() as it's handled by the RegisterPage component
        }
        catch (error) {
            setError(error instanceof Error ? error.message : 'Registration failed');
        }
    }, []);
    const setUser = useCallback((user) => {
        setState(prev => ({
            ...prev,
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
        }));
    }, []);
    const logout = useCallback(async () => {
        try {
            setLoading(true);
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include',
            });
            setState(prev => ({
                ...prev,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null
            }));
            navigate('/login', { replace: true });
        }
        catch (error) {
            setError(error instanceof Error ? error.message : 'Logout failed');
        }
    }, [navigate]);
    const value = {
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        error: state.error,
        login,
        register,
        logout,
        clearError,
        setUser
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
